import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { Helper } from "@helpers/functions";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { ModalService } from "@core-services/modal.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { TradableTileCoreComponent } from "../tradable-tile-core.component";
import { Currency } from "@models/interfaces/currency";
import { Cryptocoin, Fiat, FiatWallet } from "@models";
import { Metal } from "@models/asset/metal/metal";
import { Index } from "@models/asset/index/index.model";
import { StockTestService } from "@main-services/stock-test.service";
import { Model } from "bp-common-models-lib";
import { Stock } from "@models/derivative/currencies/stock";
import { Etf } from "@models/derivative/currencies/etf";
import {
	CURRENCY_TYPE,
	DERIVATIVE_TYPE,
	INFINITE_MIN_DISPLAY_ITEMS,
	INFINITE_SEARCH_DELAY,
	MIN_PERCENTAGE_DISPLAY,
	STEELCOIN_INTERNAL_ID,
	WATCHLIST_CHECK_MARK_EMPTY,
	WATCHLIST_CHECK_MARK_FILLED
} from "@helpers/constants";
import { convertTradableToFiat, toCurrencyByFiatId, toEuroByFiatId } from "@helpers/tradable-convert";
import { debounceTime, distinctUntilChanged, map, skip, tap, withLatestFrom } from "rxjs/operators";
import { AssetsTradeScrollService } from "@main-services/assets-trade-scroll.service";
import { BehaviorSubject, combineLatest, EMPTY, Observable } from "rxjs";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { UserService } from "@main-services/user.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Etc } from "@models/derivative/currencies/etc";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { MultichainTradableWallet } from "@modules/main/components/step-summary/sending-modal/entities/multichain-tradable-wallet";
import { TradableToListHelperService } from "@modules/main/components/tradable-to-view-item/tradable-to-list-item/services/tradable-to-list-helper.service";
import { SendMultipleWalletsSelectionComponent } from "@modules/main/components/step-summary/sending-modal/send-multiple-wallets-selection/send-multiple-wallets-selection.component";
import { SendSelectNetworkComponent } from "@modules/main/components/step-summary/sending-modal/send-select-network/send-select-network.component";
import { SendTrackingFacade } from "@modules/main/components/step-summary/sending-modal/services/send-tracking.facade";
import { SendWarningComponent } from "@modules/main/components/step-summary/sending-modal/send-warning/send-warning.component";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";

@UntilDestroy()
@Component({
	selector: "tradable-list-item",
	templateUrl: "./tradable-to-list-item.component.html",
	styleUrls: ["./tradable-to-list-item.component.scss"],
	providers: [AssetsTradeScrollService, TradableToListHelperService, SendTrackingFacade],
	standalone: false
})
export class TradableToListItemComponent extends TradableTileCoreComponent implements OnInit, OnChanges {
	@Input() public tradables: Tradable[] = [];
	@Input() public isFavourites: boolean = false;
	@Input() public selectedList: Tradable[] = [];
	@Input() public tradable: Tradable = null;
	@Input() public selectedCryptocoin: Cryptocoin = null;
	@Input() public mostPopularList: Tradable[] = [];
	@Input() public isMultichain: boolean = false;

	private hasMultipleWallets: boolean = false;
	public tradableList: BehaviorSubject<Tradable[]> = new BehaviorSubject([]);

	@Output() public onFiatSelect: EventEmitter<FiatWallet> = new EventEmitter<FiatWallet>();
	@Output() public onSelectMultichainTradable: EventEmitter<void> = new EventEmitter<void>();
	@Output() public selectedListChange: EventEmitter<Tradable[]> = new EventEmitter<Tradable[]>();
	@Output() public updatedWalletList: EventEmitter<Element> = new EventEmitter<Element>();
	private fiatWalletList: BehaviorSubject<FiatWallet[]> = new BehaviorSubject([]);

	private areSecurityTokensActive$ = this.launchDarklyService.isActive$(LD_FLAG.SECURITY_TOKENS);
	private hideSteelCoin$ = this.statsigService.isFeatureActive(FEAT_GATES.REMOVE_STEELCOIN);

	public pillsThatNeedRealSecuritiesBanner = [CURRENCY_TYPE, DERIVATIVE_TYPE];
	public currencyType = CURRENCY_TYPE;

	// ---------------------- INFINITE SCROLL SUPPORT BELOW
	//TODO: legacy approach not performative, this should have been either as signal or variable
	@Input() public customListFilter: (tradables: Tradable[]) => Tradable[]; // Custom filtering plugged in by each flow
	private tradableList$: Observable<Tradable[]> = this.assetTradeScrollService.getListObservable().pipe(
		map((tradables: Tradable[]) => this.customListFilter(tradables)),
		withLatestFrom(this.areSecurityTokensActive$, this.hideSteelCoin$),
		map(([tradables, areSecurityTokenActive, hideSteelCoin]) => {
			if (!areSecurityTokenActive) {
				return tradables.filter((tradable) => !(tradable instanceof SecurityToken));
			}
			if (hideSteelCoin) {
				return tradables.filter((tradable) => tradable.getId() !== STEELCOIN_INTERNAL_ID);
			}
			return tradables;
		}),
		tap((filteredTradables: Tradable[]) => {
			// A certain number of items are needed for scrolling to activate -> Using "12" for now

			if (
				filteredTradables.length < INFINITE_MIN_DISPLAY_ITEMS &&
				this.assetTradeScrollService.getListValue().length !== 0
			) {
				this.fetchListUpdate();
			}
		})
	);

	private areLeverageActive$ = this.launchDarklyService.isActive$(LD_FLAG.LEVERAGE);
	public isLoading$ = this.assetTradeScrollService.getLoadingObservable();
	public hasError$ = this.assetTradeScrollService.getFetchErrorObservable();
	public searchSub: BehaviorSubject<string> = new BehaviorSubject<string>("");

	// ----------------------

	public currencyFilterTypes$: BehaviorSubject<(typeof Currency)[]> = new BehaviorSubject<(typeof Currency)[]>([]);
	public currencyFilterTypesFF$: Observable<(typeof Currency)[]> = combineLatest([
		this.areLeverageActive$,
		this.currencyFilterTypes$,
		this.areSecurityTokensActive$
	]).pipe(
		map(([activeL, currencies, securityTokensActive]) => {
			// if FF is off, filter out ETCs
			if (!activeL) {
				return currencies.filter((c) => !(c instanceof LeveragedToken));
			}

			if (!securityTokensActive) {
				return currencies.filter((c) => !(c instanceof SecurityToken));
			}

			return currencies;
		})
	);

	public multichainAssetWalletInfo: Observable<MultichainTradableWallet[]> = EMPTY;
	private tradableToListHelperService = inject(TradableToListHelperService);
	private trackingService = inject(SendTrackingFacade);

	public constructor(
		protected accountService: AccountService,
		protected modalService: ModalService,
		protected walletService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected userService: UserService,
		protected stockTestService: StockTestService,
		private fiatService: FiatService,
		private assetTradeScrollService: AssetsTradeScrollService,
		protected stockTestHelperService: StockTestHelperService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly statsigService: StatsigClientService
	) {
		super(accountService, modalService, walletService, tradableListService, userService);
	}

	// ------------------------------ INFINITE SCROLL IMPLEMENTATION

	public ngOnChanges(changes: SimpleChanges): void {
		// Since tradables come from an observable, view-item-update needs to be triggered
		if (changes.hasOwnProperty("tradables") && this.hasInfiniteScrollSupport()) {
			this.updateViewItems();
		}
	}

	public onSearchKeyUp(): void {
		this.searchSub.next(this.searchInputValue);
	}

	public ngOnInit(): void {
		if (this.isMultichain) {
			this.trackingService.trackChooseAssetModalViewed();
		}
		// TODO: temp solution, will be solved "correctly" while refactoring entire crypto flow in Q3 - FADW
		if (this.selectedCryptocoin) {
			this.onItemClick(this.selectedCryptocoin);
			return;
		}

		this.assetTradeScrollService.setActiveFlow(this.action);
		this.assetTradeScrollService.initList();
		this.initializeSearchSubscription();

		this.currencyFilterTypes$.next(this.getCurrencyFilterTypes());

		if (this.isMultichain) {
			this.multichainAssetWalletInfo = this.tradableToListHelperService.getCryptoWalletInfo$();
		}
	}

	public emitSelectedWallet(wallet: TradableWallet): void {
		this.onSelect.emit(wallet);
	}

	public initializeSearchSubscription(): void {
		if (this.hasInfiniteScrollSupport()) {
			this.searchSub // The onSearch and onFilter are necessary UNTIL all flows support infinite scrolling
				.pipe(
					untilDestroyed(this),
					skip(1), // Needed to skip empty value on searchSub init!
					debounceTime(INFINITE_SEARCH_DELAY),
					distinctUntilChanged()
				)
				.subscribe((value: string) => this.assetTradeScrollService.searchListUpdate(value));
		} else {
			this.searchSub.pipe(untilDestroyed(this)).subscribe(() => this.updateViewItems());
		}
	}

	public refetchList(): void {
		this.assetTradeScrollService.refetchList([this._filterType.prototype.getCurrencyType()]);
	}

	public fetchListUpdate(): void {
		this.assetTradeScrollService.fetchListUpdate();
	}
	public filterUpdate(filter: string = ""): void {
		this.assetTradeScrollService.filterUpdate([filter], this.searchInputValue);
	}

	public hasInfiniteScrollSupport(): boolean {
		return this.assetTradeScrollService.isInfiniteScrollSupported(this.action);
	}

	public onFilter(filterType: typeof Currency): void {
		if (this.hasInfiniteScrollSupport()) {
			this._filterType = filterType;
			const filter: string = filterType.prototype.getCurrencyType();
			this.filterUpdate(filter);
		} else {
			this.filterType = filterType;
		}
	}

	protected onTradableIdChange(): void {
		// Necessary for setting back-button logic in trade component
		this.hasMultipleWallets = false;
		super.onTradableIdChange();
	}

	public getFiatWalletList(): Observable<FiatWallet[]> {
		return this.fiatWalletList.asObservable();
	}

	public getFiatFromWallet(wallet: FiatWallet): Fiat {
		return this.fiatService.getItemById(wallet.getFiatId());
	}

	private resetFilter(): void {
		this.filterType = Currency;
		this.searchInputValue = "";
	}

	public onFiatClick(wallet: FiatWallet): void {
		this.onFiatSelect.emit(wallet);
		this.resetFilter();
	}

	public onItemClick(cw: Tradable): void {
		const callback: Function = () => this.onTradableSelect(cw);
		if ((this.isBuyAction() || this.isSellAction()) && this.stockTestService.needsTest(cw.getId())) {
			this.stockTestHelperService.startTest(callback);
		} else {
			callback();
		}
	}

	public onFavouritesItemClick(tradable: Tradable): void {
		if (this.isIncluded(tradable)) {
			this.selectedList = this.selectedList.filter((item: Tradable) => item !== tradable);
		} else {
			this.selectedList.push(tradable);
		}
		this.selectedListChange.emit(this.selectedList);
	}

	public isIncluded(t: Tradable): boolean {
		return this.selectedList.includes(t);
	}

	public getIconPath(tradable: Tradable): string {
		return this.isIncluded(tradable) ? WATCHLIST_CHECK_MARK_FILLED : WATCHLIST_CHECK_MARK_EMPTY;
	}

	private onTradableSelect(cw: Tradable): void {
		this.tradableId = cw.getId();
		this.tradableIdChange.emit(this.tradableId);
		let selectedWallets: TradableWallet[] = this.walletService.getWalletsForTradable(this.tradableId);
		if (this.isSellAction()) {
			selectedWallets = selectedWallets.filter(
				(w: TradableWallet) =>
					// Voucher/Buy/Savings are all BUY Actions! This edgecase is only applicable to SELL
					w.getBalance() * this.getTradableById().getAvgPrice() >= this.getTradableById().getMinSell()
			);
		}
		if (this.isSendAction()) {
			selectedWallets = selectedWallets.filter((w: TradableWallet) => w.getBalance() > 0); // Even crypto dust can be sent
		}

		if (selectedWallets.length > 1) {
			this.hasMultipleWallets = true;
			this.walletList.next(selectedWallets);
		} else {
			this.hasMultipleWallets = false;
			this.onSelect.emit(selectedWallets[0]);
		}
		this.resetFilter();
	}

	public updateViewItems(): void {
		this.currencyFilterTypes$.next(this.getCurrencyFilterTypes()); // Updates currency filter whenever tradableList is updated

		if (this.isMultichain) {
			const fiatWallets = Helper.filterBySearch(
				this.fiatWallets,
				this.searchInputValue,
				["name"],
				[this.getFiatCurrencySymbol, this.getFiatTranslatedName]
			);
			this.fiatWalletList.next(Helper.isInheritOf(Fiat, this.filterType) ? fiatWallets : []);
			//filter crypto
			this.multichainAssetWalletInfo = this.multichainAssetWalletInfo.pipe(
				map((val) => {
					if (this.filterType.prototype.getCurrencyType() === Fiat.prototype.getCurrencyType()) {
						return [];
					}
					return val.filter(
						(wallet) =>
							wallet?.asset?.symbol?.toLowerCase().search(this.searchInputValue.toLowerCase()) !== -1 ||
							wallet?.asset?.name?.toLowerCase().search(this.searchInputValue.toLowerCase()) !== -1
					);
				})
			);
		} else {
			let tradables: Tradable[] = this.tradables;
			let fiatWallets: FiatWallet[] = this.fiatWallets;
			if (this.searchInputValue) {
				tradables = Helper.filterBySearch(tradables, this.searchInputValue, ["name", "symbol"]); // This easy search filters out tradables that are not being searched for which removes the respective wallets from the display
				fiatWallets = Helper.filterBySearch(
					fiatWallets,
					this.searchInputValue,
					["name"],
					[this.getFiatCurrencySymbol, this.getFiatTranslatedName]
				);
			}
			this.fiatWalletList.next(Helper.isInheritOf(Fiat, this.filterType) ? fiatWallets : []);
			tradables = tradables
				.filter((c: Tradable) => c instanceof this.filterType)
				.filter((c: Tradable) => (this.tradableId ? c.getId() === this.tradableId : true));
			this.tradableList.next(tradables);
		}
	}

	// ------------------------- FILTER SPECIFIC METHODS

	private getCurrencyFilterTypes(): (typeof Currency)[] {
		const defaultCurrencies = [Currency, Cryptocoin, LeveragedToken, Metal, Index, Stock, Etf, Etc, SecurityToken];

		if (this.hasInfiniteScrollSupport()) {
			return defaultCurrencies;
		}
		if (this.tradables.length === 0) {
			return [];
		}

		let currencyFilters: (typeof Currency)[] = defaultCurrencies;
		currencyFilters = currencyFilters.filter((type) =>
			this.filterByAction(
				this.tradables
					.filter((item: Tradable) => !item.isIndexOnly()) // Checks filterType array for items that are buy or sell active. If no item of a specific tradable-type is active, that filter-option is not shown
					.filter((item: Tradable) => item instanceof type)
			)
		);
		if (this.fiatWallets?.length > 0) {
			currencyFilters.push(Fiat);
		}
		return currencyFilters;
	}

	private filterByAction(items: Tradable[]): boolean {
		if (this.isBuyAction()) {
			return items.some((item: Tradable) => item.isBuyActive());
		}

		if (this.isDepositAction()) {
			return items.some((item: Tradable) => item.isDepositActive());
		}

		let walletsAboveMinSell: TradableWallet[] = [];
		return items.some((item: Tradable) => {
			walletsAboveMinSell = this.walletService
				.getNonEmptyWallets(item)
				.filter((w: TradableWallet) => !w.isDeleted())
				.filter(
					(w: TradableWallet) =>
						convertTradableToFiat(item, w.getBalance(), this.fiatService.getItemById("1")) >=
						item.getMinSell()
				);
			return walletsAboveMinSell.length > 0 && item.isSellActive(); // Sell filter checks if enough balance is in a wallet for selling to be possible
		});
	}

	// ------------ Extra search methods - not covered by the list properties. Necessary for FIAT search!
	private getFiatCurrencySymbol = <T extends Model>(wallet: T): string => {
		const currency: Currency = this.getWalletCurrency(wallet);
		return currency.getSymbol();
	};

	private getFiatTranslatedName = <T extends Model>(wallet: T): string =>
		(this.getWalletCurrency(wallet) as Fiat).getName();

	private getWalletCurrency<T extends Model>(wallet: T): Currency {
		let currency: Currency;
		if (wallet instanceof TradableWallet) {
			const tradableId = wallet.getCurrencyId();
			currency = this.tradableListService.getItemById(tradableId);
		} else if (wallet instanceof FiatWallet) {
			const fiatId = wallet.getFiatId();
			currency = this.fiatService.getItemById(fiatId);
		}
		return currency;
	}

	// ------------------------- UTILITY METHODS

	public getTradableList(): Observable<Tradable[]> {
		if (!this.tradables) {
			this.updateViewItems();
		}

		if (this.hasInfiniteScrollSupport()) {
			return this.tradableList$;
		}
		return this.tradableList.asObservable();
	}

	public getTradableById(): Tradable {
		return this.tradableListService.getItemById(this.tradableId);
	}

	public showMultipleWallets(): boolean {
		return this.hasMultipleWallets;
	}

	public getAveragePrice(tradable: Tradable): number {
		return toCurrencyByFiatId(tradable.getAvgPrice(), this.fiat);
	}

	public getAbsoluteChange(tradable: Tradable): number {
		return Math.abs(tradable.getChange24h());
	}

	public isChangePositive(tradable: Tradable): boolean {
		return tradable.getChange24h() >= MIN_PERCENTAGE_DISPLAY;
	}

	public isChangeNegative(tradable: Tradable): boolean {
		return tradable.getChange24h() <= -MIN_PERCENTAGE_DISPLAY;
	}

	public isBuyAction(): boolean {
		// Remove flows when infinite scroll is implemented
		return this.action === "buy" || this.action === "voucher";
	}

	private isSendAction(): boolean {
		return this.action === "send";
	}

	private isSellAction(): boolean {
		return this.action === "sell";
	}

	private isDepositAction(): boolean {
		return this.action === "deposit";
	}

	public getWalletSumFiat(tradable: Tradable): number {
		return this.calculateWalletSum(this.getWalletSum(tradable), tradable);
	}

	public getWalletSum(c: Tradable): number {
		return this.getWallets(c).reduce((val: number, w: TradableWallet) => val + w.getBalance(), 0);
	}

	private getWallets(c: Tradable): TradableWallet[] {
		let wallets: TradableWallet[] = this.walletService.list;
		wallets = wallets.filter((w: TradableWallet) => w.getCurrencyId() === c.getId() && !w.isDeleted());
		return wallets;
	}

	public isDefaultEuro(): boolean {
		return this.getDefaultFiat().getId() === "1"; // Will be moved to constants in dedicated issue, 1 stands for EUR
	}

	public convertBalanceToDefaultFiat(wallet: FiatWallet): number {
		let defaultFiat = this.getDefaultFiat();
		if (defaultFiat.getId() !== wallet.getFiatId()) {
			if (this.isDefaultEuro()) {
				return toEuroByFiatId(wallet.getBalance(), this.getFiatFromWallet(wallet));
			}
			return (
				(wallet.getBalance() * (this.getWalletCurrency(wallet) as Fiat).toEuroRate()) / defaultFiat.toEuroRate()
			);
		}
		return wallet.getBalance();
	}

	public getDefaultFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public showChangeTooltip(): boolean {
		return this.isBuyAction();
	}

	private getWalletsForMultichain(mTradable: PaymentsCryptocoin): TradableWallet[] {
		let wallets: TradableWallet[] = this.walletService.list;
		wallets = wallets.filter((w: TradableWallet) => w.getCurrencyId() === mTradable.id && !w.isDeleted());
		return wallets;
	}

	public getMultichainWalletSum(mTradeable: PaymentsCryptocoin): number {
		return this.getWalletsForMultichain(mTradeable).reduce(
			(val: number, w: TradableWallet) => val + w.getBalance(),
			0
		);
	}

	public getMultichainWalletSumFiat(mTradeable: PaymentsCryptocoin): number {
		const respectiveTradable = this.tradables.find((item) => item.getPid() === mTradeable.pid);
		return this.getWalletSumFiat(respectiveTradable);
	}

	public clickedMultichainTradable(mTradeable: MultichainTradableWallet): void {
		this.onSelectMultichainTradable.emit();
		this.trackingService.trackAssetClickedInChooseAssetModal(mTradeable.asset.symbol.toUpperCase());
		this.tradableId = mTradeable.asset.cryptocoin_id.toString();
		let selectedWallets: TradableWallet[] = this.walletService.getWalletsForTradable(
			mTradeable.asset.cryptocoin_id.toString()
		);
		if (this.isSendAction()) {
			selectedWallets = selectedWallets.filter((w: TradableWallet) => w.getBalance() > 0); // Even crypto dust can be sent
		}

		if (selectedWallets.length > 1) {
			this.modalService
				.create(SendMultipleWalletsSelectionComponent, {
					walletList: selectedWallets,
					tradable: mTradeable.asset
				})
				.present();
		} else {
			const availableNetworks = mTradeable.asset.networks.filter(
				(val) => val.is_withdraw_allowed && val.is_operational
			);
			const unavailableNetworks = mTradeable.asset.networks.filter(
				(val) => !(val.is_withdraw_allowed && val.is_operational)
			);
			if (availableNetworks.length === 1) {
				this.modalService
					.create(SendWarningComponent, {
						selectedCryptocoin: mTradeable.asset,
						selectedNetwork: availableNetworks[0],
						walletId: selectedWallets[0].getId()
					})
					.present();
			} else {
				this.modalService
					.create(SendSelectNetworkComponent, {
						availableNetworks,
						unavailableNetworks,
						selectedCryptocoin: mTradeable.asset,
						walletId: selectedWallets[0].getId()
					})
					.present();
			}
		}
	}
}
